import { ApiError } from "@/lib/api";
/* oxlint-disable react-doctor/query-mutation-missing-invalidation */
import { useMutation } from "@tanstack/react-query";
import { Check, Sparkles, X } from "lucide-react";
import { type JSX, useState } from "react";
import { Link } from "react-router";

import { DraftDiff } from "@/components/ui/draft-diff";
import { refineCommitSource } from "@/lib/sources";

export type RefineKnowledgeCardStatus = "pending" | "approved" | "cancelled";

export interface RefineKnowledgeCardState {
  itemId: string;
  /** The item's full text when the card was created: the old side of the diff.
   * It is not the banner's capped preview, and it lives here so the diff does
   * not depend on the banner still being mounted. */
  currentText: string;
  refinedText: string;
  status: RefineKnowledgeCardStatus;
  /** Set when the operator approved the refine — used by the deep-link. */
  committedAt?: string;
}

interface RefineKnowledgeCardProps {
  state: RefineKnowledgeCardState;
  onUpdate: (next: RefineKnowledgeCardState) => void;
}

export function RefineKnowledgeCard({ state, onUpdate }: RefineKnowledgeCardProps): JSX.Element {
  if (state.status === "approved") {
    return (
      <div className="mt-3 rounded-md border border-brand-feedback-success-00/30 bg-brand-feedback-success-00/5 px-3 py-2">
        <div className="flex items-start gap-2">
          <Check size={16} className="mt-0.5 text-brand-feedback-success-00" />
          <div className="flex-1">
            <p className="mb-1 text-label-2 uppercase tracking-wider text-muted-foreground">
              conocimiento actualizado
            </p>
            <p className="text-body-2 text-foreground">{state.refinedText}</p>
            <Link
              to={`/settings/sources?type=chat&highlight=${state.itemId}`}
              className="mt-1 inline-block text-label-2 text-brand-extra-2-40 underline-offset-2 hover:underline"
            >
              ver en /sources
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (state.status === "cancelled") {
    return (
      <div className="mt-3 rounded-md border border-border bg-muted/50 px-3 py-2">
        <p className="text-body-2 text-muted-foreground">{state.refinedText}</p>
        <p className="mt-1 text-label-2 uppercase tracking-wider text-muted-foreground">
          cancelado — el conocimiento queda sin cambios
        </p>
      </div>
    );
  }

  return <PendingCard state={state} onUpdate={onUpdate} />;
}

function PendingCard({ state, onUpdate }: RefineKnowledgeCardProps): JSX.Element {
  const [unchangedNote, setUnchangedNote] = useState(false);

  const approveMutation = useMutation({
    mutationFn: () => refineCommitSource(state.itemId, state.refinedText),
    onSuccess: () => {
      onUpdate({
        ...state,
        status: "approved",
        committedAt: new Date().toISOString(),
      });
    },
    onError: (error: unknown) => {
      if (
        error instanceof ApiError &&
        error.status === 409 &&
        error.message.includes("content_unchanged")
      ) {
        setUnchangedNote(true);
        return;
      }
      setUnchangedNote(false);
    },
  });

  function handleCancel(): void {
    onUpdate({
      ...state,
      status: "cancelled",
    });
  }

  const disabled = approveMutation.isPending;
  const isContentUnchangedError =
    approveMutation.error instanceof ApiError &&
    approveMutation.error.status === 409 &&
    approveMutation.error.message.includes("content_unchanged");
  const otherError =
    approveMutation.error instanceof Error && !isContentUnchangedError
      ? approveMutation.error.message
      : null;

  return (
    <div className="mt-3 rounded-md border border-border bg-background px-3 py-2">
      <div className="flex items-start gap-2">
        <Sparkles size={16} className="mt-0.5 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="mb-1 text-label-2 uppercase tracking-wider text-muted-foreground">
            propuesta de refinamiento
          </p>
          <DraftDiff
            before={state.currentText}
            after={state.refinedText}
            beforeLabel="Texto actual"
            afterLabel="Texto propuesto"
            label="Diferencias entre el texto actual y el propuesto"
          />
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => approveMutation.mutate()}
              disabled={disabled}
              className="inline-flex items-center gap-1 rounded-md bg-brand-extra-2-40 px-3 py-1 text-label-2 font-semibold text-white hover:opacity-90 disabled:opacity-50"
              data-testid="refine-knowledge-approve"
            >
              <Check size={14} aria-hidden />
              Aprobar reemplazo
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={disabled}
              className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1 text-label-2 text-foreground hover:bg-muted disabled:opacity-50"
              data-testid="refine-knowledge-cancel"
            >
              <X size={14} aria-hidden />
              Cancelar
            </button>
          </div>
          {unchangedNote || isContentUnchangedError ? (
            <p
              className="mt-2 text-label-2 text-amber-400"
              data-testid="refine-knowledge-unchanged"
            >
              el texto propuesto es idéntico al actual — no hay nada que guardar
            </p>
          ) : null}
          {otherError ? (
            <p role="alert" className="mt-2 text-label-2 text-destructive">
              {otherError}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
